import { t } from '../../common/i18n/index.js';
import { DeliveryType } from '../../generated/api/deliveryType.js';
import type { CommonError } from '../../common/types/errors.js';
import type { DeliveryMethod } from '../../generated/api/deliveryMethod.js';
import type { Address } from '../../generated/api/models.js';
import type { OnlineOrder } from '../../generated/api/onlineOrder.js';
import type { PickupPoint } from '../../generated/api/pickupPoint.js';
import type { DeliveryAddressType } from '../DeliveryAddress/DeliveryAddress.js';
export enum DeliveryModeType {
  COLLECT_POINT = 'COLLECT_POINT',
  ADDRESS = 'ADDRESS',
}

const POSTI_LOGO_CLASS = 'posti';
const POSTI_SEARCH_SERVICE = 'posti';

export const DELIVERY_METHOD_DETAILS = new Map<string, DeliveryMethodDetails>([
  [
    DeliveryType.POSTI_EXPRESS_BUSINESS,
    {
      additionalInformationLink: '',
      createDescription: () => t.LLQR('Delivery to address. Delivered next working day by 4 PM'),
      id: 'Y3',
      notes: '',
      price: 790,
      type: DeliveryType.POSTI_EXPRESS_BUSINESS,
      image: POSTI_LOGO_CLASS,
      deliveryModeType: DeliveryModeType.ADDRESS,
    },
  ],
  [
    DeliveryType.POSTI_SMARTPOST,
    {
      additionalInformationLink: '',
      createDescription: () => t.Z0OY('Pick up at Posti Parcel Point'),
      id: 'P1',
      notes: '',
      price: 590,
      type: DeliveryType.POSTI_SMARTPOST,
      image: POSTI_LOGO_CLASS,
      searchService: POSTI_SEARCH_SERVICE,
      deliveryModeType: DeliveryModeType.COLLECT_POINT,
    },
  ],
]);

export const getAddressDeliveryMethods = (deliveryMethods?: DeliveryMethod[]): DeliveryMethodDetails[] => {
  return deliveryMethods
    ?.filter(item => DELIVERY_METHOD_DETAILS.get(item.deliveryTypeCode))
    ?.map(items => {
      return { ...DELIVERY_METHOD_DETAILS.get(items.deliveryTypeCode), price: items.price };
    })
    .filter(item => item.deliveryModeType === DeliveryModeType.ADDRESS) as DeliveryMethodDetails[];
};

export const getCollectPointDeliveryMethods = (deliveryMethods?: DeliveryMethod[]): DeliveryMethodDetails[] => {
  return deliveryMethods
    ?.filter(item => DELIVERY_METHOD_DETAILS.get(item.deliveryTypeCode))
    ?.map(items => {
      return { ...DELIVERY_METHOD_DETAILS.get(items.deliveryTypeCode), price: items.price };
    })
    .filter(item => item.deliveryModeType === DeliveryModeType.COLLECT_POINT) as DeliveryMethodDetails[];
};

export const DELIVERY_MODES: DeliveryMode[] = [
  {
    type: DeliveryModeType.ADDRESS,
    icon: 'delivery',
    createText: () => t.ZQ4R('Delivery to address'),
    getDeliveryMethods: getAddressDeliveryMethods,
  },
  {
    type: DeliveryModeType.COLLECT_POINT,
    icon: 'location',
    createText: () => t.TWHW('Collect point'),
    getDeliveryMethods: getCollectPointDeliveryMethods,
  },
];

export const getAllDeliveryMethods = (deliveryMethods?: DeliveryMethod[]): DeliveryMethodDetails[] => {
  return deliveryMethods
    ?.filter(item => DELIVERY_METHOD_DETAILS.get(item.deliveryTypeCode))
    ?.map(item => {
      return { ...DELIVERY_METHOD_DETAILS.get(item.deliveryTypeCode), price: item.price };
    }) as DeliveryMethodDetails[];
};

export const getDefaultDeliveryMethod = (deliveryMethods?: DeliveryMethod[]): DeliveryMethodDetails => {
  return getAddressDeliveryMethods(deliveryMethods)[0];
};

export interface DeviceCheckoutDeliveryDetailsType {
  address?: Address;
  addressType?: DeliveryAddressType;
  deliveryMethodType: DeliveryType;
  deliveryPrice: number;
  deliveryMethodDescription: string;
  recipientName?: string;
  recipientPhoneNumber?: string;
  pickupPoint?: PickupPoint;
  companyName?: string;
  shipmentType?: OnlineOrder.ShipmentTypeEnum;
}

export interface DeliveryMethodDetails {
  id: string;
  notes: string;
  createDescription: () => string;
  type: DeliveryType;
  additionalInformationLink?: string;
  price: number;
  image: string;
  searchService?: string;
  isDirectDeliveryAllowed?: boolean;
  deliveryModeType?: DeliveryModeType;
}

export interface DeliveryMode {
  type: DeliveryModeType;
  icon: string;
  createText: () => string;
  getDeliveryMethods: (deliveryMethods?: DeliveryMethod[]) => DeliveryMethodDetails[];
}

export interface DeviceCheckoutDeliveryDetailsBaseProps {
  deliveryDetails: DeviceCheckoutDeliveryDetailsType;
  eppDevicePresentInCart?: boolean;
  saveDeliveryDetailsToState: (
    deliveryDetails: DeviceCheckoutDeliveryDetailsType,
    validationErrors?: CommonError[]
  ) => void;
  footerNote?: string;
  skipDeliveryMethods?: boolean;
  listOfDeliveryMethods?: DeliveryMethod[];
}

export type ErrorType = { [key: string]: string };

export const validateDeliveryDetails = (
  selectedDeliveryMode: DeliveryMode,
  listOfDeliveryMethods: DeliveryMethod[] | undefined,
  isEditingAddress: boolean,
  pickupPoint: PickupPoint | undefined,
  isPickupPointRequired: boolean,
  isAddressChanged: boolean = false
): ErrorType => {
  const errors: ErrorType = {};

  if (!selectedDeliveryMode) {
    errors.selectedDeliveryMode = t.C499('Please select a delivery mode.');
  }

  if (!listOfDeliveryMethods) {
    errors.listOfDeliveryMethods = t.K2D6('Please select a delivery method.');
  }

  if (isAddressChanged || (isEditingAddress && selectedDeliveryMode?.type === DeliveryModeType.ADDRESS)) {
    errors.isEditingAddress = t.ZF4E('Please finish editing the address before proceeding.');
  }

  if (!pickupPoint && isPickupPointRequired) {
    errors.pickupPoint = t.N3A8('Please select a pickup point.');
  }

  return errors;
};
